var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { readonly, ref } from "vue";
import { fetcher } from "@/api/schema";
import { withPrefix } from "@/utils/redirect";
export const DEFAULT_EXPORT_PARAMS = {
    modelStoreFormat: "rocrate.zip",
    includeFiles: true,
    includeDeleted: false,
    includeHidden: false,
};
const DEFAULT_POLL_DELAY = 1000;
const DEFAULT_OPTIONS = { exportParams: DEFAULT_EXPORT_PARAMS, pollDelayInMs: DEFAULT_POLL_DELAY };
const startPreparingHistoryDownload = fetcher
    .path("/api/histories/{history_id}/prepare_store_download")
    .method("post")
    .create();
const startPreparingInvocationDownload = fetcher
    .path("/api/invocations/{invocation_id}/prepare_store_download")
    .method("post")
    .create();
const getTempStorageRequestReady = fetcher
    .path("/api/short_term_storage/{storage_request_id}/ready")
    .method("get")
    .create();
/**
 * Composable to simplify and reuse the logic for downloading objects using Galaxy's Short Term Storage system.
 */
export function useShortTermStorage() {
    let timeout = null;
    let pollDelay = DEFAULT_POLL_DELAY;
    const isPreparing = ref(false);
    const forHistory = (id, params) => __awaiter(this, void 0, void 0, function* () {
        const { data } = yield startPreparingHistoryDownload(Object.assign({ history_id: id }, params));
        return data;
    });
    const forInvocation = (id, params) => __awaiter(this, void 0, void 0, function* () {
        const { data } = yield startPreparingInvocationDownload(Object.assign({ invocation_id: id }, params));
        return data;
    });
    function prepareHistoryDownload(historyId, options = DEFAULT_OPTIONS) {
        return __awaiter(this, void 0, void 0, function* () {
            return prepareObjectDownload(forHistory, historyId, options, false);
        });
    }
    function downloadHistory(historyId, options = DEFAULT_OPTIONS) {
        return __awaiter(this, void 0, void 0, function* () {
            return prepareObjectDownload(forHistory, historyId, options, true);
        });
    }
    function prepareWorkflowInvocationDownload(invocationId, options = DEFAULT_OPTIONS) {
        return __awaiter(this, void 0, void 0, function* () {
            return prepareObjectDownload(forInvocation, invocationId, options, false);
        });
    }
    function downloadWorkflowInvocation(invocationId, options = DEFAULT_OPTIONS) {
        return __awaiter(this, void 0, void 0, function* () {
            return prepareObjectDownload(forInvocation, invocationId, options, true);
        });
    }
    function getDownloadObjectUrl(storageRequestId) {
        const url = withPrefix(`/api/short_term_storage/${storageRequestId}`);
        return url;
    }
    function downloadObjectByRequestId(storageRequestId) {
        const url = getDownloadObjectUrl(storageRequestId);
        window.location.assign(url);
    }
    function prepareObjectDownload(startPreparingDownloadAsync, objectId, options = DEFAULT_OPTIONS, downloadWhenReady = true) {
        return __awaiter(this, void 0, void 0, function* () {
            resetTimeout();
            isPreparing.value = true;
            const finalOptions = Object.assign(DEFAULT_OPTIONS, options);
            pollDelay = finalOptions.pollDelayInMs;
            const exportParams = {
                model_store_format: finalOptions.exportParams.modelStoreFormat,
                include_files: finalOptions.exportParams.includeFiles,
                include_deleted: finalOptions.exportParams.includeDeleted,
                include_hidden: finalOptions.exportParams.includeHidden,
            };
            try {
                const response = yield startPreparingDownloadAsync(objectId, exportParams);
                const storageRequestId = response.storage_request_id;
                pollStorageRequestId(storageRequestId, downloadWhenReady);
            }
            catch (err) {
                stopPreparing();
            }
        });
    }
    function pollStorageRequestId(storageRequestId, downloadWhenReady) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { data: ready } = yield getTempStorageRequestReady({ storage_request_id: storageRequestId });
                if (ready) {
                    isPreparing.value = false;
                    if (downloadWhenReady) {
                        downloadObjectByRequestId(storageRequestId);
                    }
                }
                else {
                    pollAfterDelay(storageRequestId, downloadWhenReady);
                }
            }
            catch (err) {
                stopPreparing();
            }
        });
    }
    function stopPreparing() {
        isPreparing.value = false;
    }
    function pollAfterDelay(storageRequestId, downloadWhenReady) {
        resetTimeout();
        timeout = setTimeout(() => {
            pollStorageRequestId(storageRequestId, downloadWhenReady);
        }, pollDelay);
    }
    function resetTimeout() {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
    }
    return {
        /**
         * Starts preparing a history download file in the short term storage.
         * @param {String} historyId The ID of the history to be prepared for download
         * @param {Object} options Options for the download preparation
         */
        prepareHistoryDownload,
        /**
         * Prepares a history download file in the short term storage and starts the download when ready.
         * @param {String} historyId The ID of the history to be downloaded
         * @param {Object} options Options for the download preparation
         */
        downloadHistory,
        /**
         * Starts preparing a workflow invocation download file in the short term storage.
         * @param {String} invocationId The ID of the workflow invocation to be prepared for download
         * @param {Object} options Options for the download preparation
         */
        prepareWorkflowInvocationDownload,
        /**
         * Starts preparing a workflow invocation download file in the short term storage and starts the download when ready.
         * @param {String} invocationId The ID of the workflow invocation to be downloaded
         * @param {Object} options Options for the download preparation
         */
        downloadWorkflowInvocation,
        /**
         * Starts a direct download of the object associated with the given `storageRequestId`.
         * For the download to succeed, the associated `storageRequestId` must be `ready` and not in failure state.
         * @param {String} storageRequestId The storage request ID associated to the object to be downloaded
         */
        downloadObjectByRequestId,
        /**
         * Whether the download is still being prepared.
         */
        isPreparing: readonly(isPreparing),
        /**
         * Given a storageRequestId it returns the download URL for that object.
         * @param {String} storageRequestId The storage request ID associated to the object to be downloaded
         */
        getDownloadObjectUrl,
    };
}
